import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import {
  NbCardModule,
  NbPopoverModule,
  NbSelectModule,
  NbSpinnerModule,
} from '@nebular/theme';
import { Apollo } from 'apollo-angular';
import { SubSink } from 'subsink';
import {
  GET_WORKER_COUNTING_ANALYTICS,
  WorkerCountingAnalytics,
} from '../../../../@core/graphql/queries/equipment-counting-journal/getWorkerCountingAnalytics.query';

@Component({
    selector: 'stack-mono-worker-counting-analytics',
    templateUrl: './worker-counting-analytics.component.html',
    styleUrls: ['./worker-counting-analytics.component.scss'],
    imports: [
    NbCardModule,
    NbPopoverModule,
    NbSelectModule,
    NbSpinnerModule
]
})
export class WorkerCountingAnalyticsComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() externalFilters: any = {};
  @Input() sortBy = 'total_count';
  @Output() workerClick = new EventEmitter<{
    countedBy: string;
    countedByName: string;
  }>();

  private sub = new SubSink();
  workerAnalytics: WorkerCountingAnalytics[] = [];
  isLoading = false;

  constructor(private apollo: Apollo) {}

  ngOnInit(): void {
    this.loadWorkerAnalytics();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['externalFilters'] || changes['sortBy']) {
      this.loadWorkerAnalytics();
    }
  }

  private loadWorkerAnalytics(): void {
    this.isLoading = true;

    this.sub.sink = this.apollo
      .watchQuery({
        query: GET_WORKER_COUNTING_ANALYTICS,
        variables: {
          filter: this.externalFilters || {},
          sortBy: this.sortBy,
          limit: 20,
        },
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.subscribe({
        next: (result: any) => {
          const data = result.data?.getWorkerCountingAnalytics;
          if (data && Array.isArray(data)) {
            this.workerAnalytics = data.map((worker: any) => ({
              countedBy: worker.countedBy,
              countedByName: worker.countedByName,
              locationNum: worker.locationNum || '',
              totalLines: worker.totalLines || 0,
              countedLines: worker.countedLines || 0,
              completionPercentage: worker.completionPercentage || 0,
              waitingCountLines: worker.waitingCountLines || 0,
              notFoundLines: worker.notFoundLines || 0,
              addedLines: worker.addedLines || 0,
            }));
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading worker counting analytics:', error);
          this.isLoading = false;
        },
      });
  }

  getWorkerInitials(name: string): string {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  onWorkerClick(worker: WorkerCountingAnalytics): void {
    this.workerClick.emit({
      countedBy: worker.countedBy,
      countedByName: worker.countedByName || 'Unknown',
    });
  }

  /**
   * Returns a blue color with the specified opacity for background gradient
   */
  getBlueColorRgba(opacity = 0.2): string {
    return `rgba(14, 74, 255, ${opacity})`; // Blue color #0e4aff
  }

  /**
   * Calculate the percentage of this worker's count relative to the max worker
   * This is used for the progress bar background effect
   */
  getWorkerPercentage(worker: WorkerCountingAnalytics): number {
    if (!this.workerAnalytics || this.workerAnalytics.length === 0) {
      return 0;
    }
    const maxLines = Math.max(...this.workerAnalytics.map((w) => w.totalLines));
    if (maxLines === 0) {
      return 0;
    }
    return (worker.totalLines / maxLines) * 100;
  }
}
